import React, { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Skeleton } from '../../../../components';
import type { PulseMetric } from '../../../../mock-data/types';
import { SectionCard } from '../SectionCard';
import { PulseBadge } from '../PulseBadge';
import { Grid, Metric, IconSlot, Body, Label, Value, ValueText, Foot, Note } from './ThisMonth.styles';

/** One metric panel — metallic icon on the left; label, figure, then a green
 *  badge and/or a muted note stacked to its right. The label is static chrome;
 *  the figure + badge/note load in. */
const MetricPanel = ({ m, loading }: { m: PulseMetric; loading: boolean }): React.ReactElement => {
  const { t } = useTranslation();

  // The metric name is a fixed headline-metric vocabulary, and "vs last" is chrome;
  // both are translated here keyed off their stable English source. Notes carrying
  // record-specific figures (e.g. "32% margin") pass through untranslated.
  const METRIC_LABELS: Record<string, string> = {
    Revenue: t('Revenue'),
    'Net Revenue': t('Net Revenue'),
    'New Players': t('New Players'),
    'Marketing ROI': t('Marketing ROI'),
  };
  const NOTE_LABELS: Record<string, string> = {
    'vs last': t('vs last'),
  };
  const label = METRIC_LABELS[m.label] ?? m.label;
  const note = m.note ? NOTE_LABELS[m.note] ?? m.note : undefined;

  return (
  <Metric>
    <IconSlot>
      <img src={m.icon} alt="" />
    </IconSlot>
    <Body>
      <Label text={label} tag="span" />
      {/* Wrapped in `Value` so the placeholder inherits its font-size (em-based).
          A `missing` figure has no value coming (e.g. Marketing ROI has no
          marketing-cost source), so it shows `?` immediately rather than a skeleton
          that resolves to nothing — mirroring the Stats `LiveNumber` convention. */}
      <Value>
        {m.missing ? (
          <ValueText data-pulse-value>?</ValueText>
        ) : loading ? (
          <Skeleton width={`${Math.max(3, m.value.length)}ch`} height="0.8em" radius={6} />
        ) : (
          <ValueText data-pulse-value>{m.value}</ValueText>
        )}
      </Value>
      <Foot>
        {m.missing ? (
          // The explanatory note (why it's `?`) shows immediately; no trend badge.
          note ? <Note text={note} tag="span" $green={false} /> : null
        ) : loading ? (
          <Skeleton width={58} height={18} radius={8} />
        ) : (
          <React.Fragment>
            {m.badge ? <PulseBadge text={m.badge} down={m.badgeDown} /> : null}
            {note ? <Note text={note} tag="span" $green={!!m.noteGreen} /> : null}
          </React.Fragment>
        )}
      </Foot>
    </Body>
  </Metric>
  );
};

export type ThisMonthProps = {
  metrics: PulseMetric[];
  /** Reveal in-progress (no-backing-field) metrics as `?`; when false they are omitted. */
  showInProgress?: boolean;
  /** While true, every metric's figure + badge show placeholders (the endpoint is loading). */
  loading?: boolean;
};

// Horizontal chrome that flanks the figure inside one card at two-up: padding
// (12 × 2) + icon (30) + icon→body gap (12). Mirrors Metric/IconSlot in the
// styles file. COL_GAP mirrors the grid `gap`. COMFORT keeps the figures off the
// card edge so we collapse a hair before they actually clip.
const CARD_CHROME = 66;
const COL_GAP = 5;
const COMFORT = 4;

/** "This month" — a grid of headline metrics (Revenue, Net Revenue, New Players,
 *  Marketing ROI). Two per row when the figures fit; collapses to one full-row
 *  column when any figure is too wide to sit two-up (e.g. larger amounts on a
 *  narrow mobile screen). */
export const ThisMonth = ({ metrics, showInProgress = false, loading = false }: ThisMonthProps): React.ReactElement => {
  const { t } = useTranslation();
  const gridRef = useRef<HTMLDivElement>(null);
  const [oneCol, setOneCol] = useState(false);

  // Drop in-progress (no-backing-field) tiles unless the flag reveals them.
  const visible = showInProgress ? metrics : metrics.filter((m) => !m.missing);

  useLayoutEffect(() => {
    const grid = gridRef.current;
    if (!grid || typeof ResizeObserver === 'undefined') return undefined;

    const measure = (): void => {
      const figures = grid.querySelectorAll<HTMLElement>('[data-pulse-value]');
      let widest = 0;
      figures.forEach((f) => {
        widest = Math.max(widest, f.getBoundingClientRect().width);
      });
      if (widest === 0) return; // loading / no figures yet — keep current decision
      // Width two cards (figure + chrome) plus the gap between them would need.
      const needed = 2 * (widest + CARD_CHROME + COMFORT) + COL_GAP;
      setOneCol(needed > grid.clientWidth);
    };

    measure();
    // Re-decide as the card resizes (rotation, split-view, font load).
    const observer = new ResizeObserver(measure);
    observer.observe(grid);
    return () => observer.disconnect();
  }, [visible, loading]);

  return (
    <SectionCard title={t('This month')}>
      <Grid ref={gridRef} $oneCol={oneCol}>
        {visible.map((m) => (
          <MetricPanel key={m.label} m={m} loading={loading} />
        ))}
      </Grid>
    </SectionCard>
  );
};

export default ThisMonth;
